import { useTranslation } from "react-i18next";
import { segmentedItem, segmentedStrip } from "@/components/ui/segmented";
import { type AccountFilter, useLokaleAccountsPaged } from "./useLokaleAccounts";

const FILTERS = ["alle", "wacht", "afwijkend"] as const;

/**
 * Which slice of the accounts list is on screen. The two narrow ones used to be a screen of their
 * own - Koppelingen - over the same table, which meant a beheerder looking at a row had to change
 * screens to reach half the actions on it.
 *
 * Buttons with a count rather than a dropdown, and that is the whole reason this control exists.
 * ADR-0018 flags a known-wrong link so that somebody checks it, and a count nobody has to open a
 * menu to see is what keeps the flag checkable once the rows sit in a list of every account.
 *
 * Drawn by `segmented`, the same strip the wedstrijddag detail pane's tabs use. Toggle buttons
 * rather than tabs, though, and that is not a detail: these reveal no tabpanel, they change what
 * the list asks the server for. See the note in `segmented.ts`.
 */
export function AccountFilters({
  value,
  onChange,
}: {
  value: AccountFilter;
  onChange: (filter: AccountFilter) => void;
}) {
  const { t } = useTranslation("lokaleAccounts");

  // One row each, for the count alone: `totalCount` is what the strip needs and a page of rows is
  // not. Both share a cache entry with nothing else, so switching filters refetches neither.
  const wacht = useLokaleAccountsPaged("wacht", 1, 1, "", undefined, true);
  const afwijkend = useLokaleAccountsPaged("afwijkend", 1, 1, "", undefined, true);

  // Through Number: the generated contract types an int32 as `number | string`, because the API
  // accepts either on the way in and openapi-typescript reads one schema for both directions.
  const aantal = (filter: AccountFilter) => {
    const total =
      filter === "wacht"
        ? wacht.data?.totalCount
        : filter === "afwijkend"
          ? afwijkend.data?.totalCount
          : undefined;

    return total === undefined ? undefined : Number(total);
  };

  return (
    <div className={segmentedStrip} role="group" aria-label={t("filter.label")}>
      {FILTERS.map((filter) => {
        const count = aantal(filter);

        return (
          <button
            key={filter}
            type="button"
            className={segmentedItem}
            aria-pressed={value === filter}
            onClick={() => onChange(filter)}
          >
            {t(`filter.${filter}`)}
            {/* Hidden at zero rather than shown as "(0)": nothing is waiting, and a zero beside a
                label reads as a thing to go and look at. */}
            {count !== undefined && count > 0 && (
              <span className="text-xs opacity-70">{count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
